import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import card_services from "services/card_services";
import merchant_services from "services/merchant_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";
import { sameToken } from "utils/tokens";
import type { NetworkType } from "utils/network";
import type {
  ApprovalAudit,
  ApprovalRow,
  ApprovalTokenRef,
  GrantResult,
  GrantRow,
} from "types/approvals";

export type ApprovalSubject = "card" | "merchant";
export type ApprovalStep = 1 | 2;

/**
 * What step 1 concluded. `unavailable` covers both a subject the chain holds no wallet for and a
 * read that failed per-row — answered, but nothing to act on either way.
 */
export type ApprovalOutcome =
  | "checking"
  | "failed"
  | "unavailable"
  | "complete"
  | "missing";

interface SubjectApi {
  noun: string;
  check: (id: string, network: NetworkType) => Promise<ApprovalAudit>;
  grant: (
    id: string,
    token: string,
    network: NetworkType,
  ) => Promise<GrantResult>;
}

const SUBJECT: Record<ApprovalSubject, SubjectApi> = {
  card: {
    noun: "card",
    check: card_services.checkTokenApprovals,
    grant: card_services.grantTokenApproval,
  },
  merchant: {
    noun: "merchant",
    check: merchant_services.checkTokenApprovals,
    grant: merchant_services.grantTokenApproval,
  },
};

/** Never empty, so no token renders unnamed. The audit sends a symbol; this is the fallback. */
export function tokenRefLabel(token: ApprovalTokenRef | undefined): string {
  if (!token) return "";
  return token.symbol || token.address;
}

export interface UseTokenApprovalsReturn {
  step: ApprovalStep;
  outcome: ApprovalOutcome;
  /** The one sentence for a non-actionable outcome — a failure, a miss, or nothing to do. */
  noticeMsg: string;
  /** A refetch is in flight. Distinct from `outcome`, which keeps showing the last answer. */
  isChecking: boolean;
  recheck: () => void;
  /** The wallet the allowance is granted FROM: a card's alias, or the merchant address. */
  walletAddress: string | null;
  subjectStatus: string;
  /** The payments contract — the spender every allowance names. */
  spender: string;
  approved: ApprovalTokenRef[];
  /** Missing, minus anything granted in this session — see the note on `grantedNow`. */
  missing: ApprovalTokenRef[];
  selectedToken: ApprovalTokenRef | undefined;
  selectToken: (address: string) => void;
  goToGrant: () => void;
  goBack: () => void;
  isGranting: boolean;
  grantErrorMsg: string;
  grantSuccessMsg: string;
  grant: () => Promise<void>;
  dismissGrantError: () => void;
  /** Dismiss the success message and return to step 1 with the granted token dropped. */
  acknowledgeGrant: () => void;
}

export function useTokenApprovals(
  kind: ApprovalSubject,
  id: string,
  enabled: boolean,
): UseTokenApprovalsReturn {
  const networkType = useSelector(networkTypeSelector);
  const queryClient = useQueryClient();
  const api = SUBJECT[kind];

  const [step, setStep] = useState<ApprovalStep>(1);
  const [pickedAddress, setPickedAddress] = useState<string | undefined>();
  const [isGranting, setIsGranting] = useState(false);
  const [grantErrorMsg, setGrantErrorMsg] = useState("");
  const [grantSuccessMsg, setGrantSuccessMsg] = useState("");

  /**
   * Tokens this session has already granted, lowercased.
   *
   * The grant response is the authoritative answer, and a re-read of the allowance can still lag
   * a block behind it — so what has been granted is subtracted locally rather than waited for.
   */
  const [grantedNow, setGrantedNow] = useState<string[]>([]);

  const auditQuery = useQuery({
    queryKey: ["approvals", kind, id],
    queryFn: () => api.check(id, networkType),
    enabled: enabled && !!id,
    // Each subject costs (1 + supported tokens) eth_calls, so this does not re-run on its own.
    refetchOnWindowFocus: false,
    retry: false,
  });

  const audit: ApprovalAudit | undefined = auditQuery.data;
  // One subject per call, so one row. Anything else is a shape we do not understand.
  const row: ApprovalRow | undefined = audit?.results?.[0];

  const approved: ApprovalTokenRef[] = useMemo(
    () => row?.approved ?? [],
    [row],
  );

  const missing: ApprovalTokenRef[] = useMemo(() => {
    const rows: ApprovalTokenRef[] = row?.missing ?? [];
    return rows.filter((t) => !grantedNow.includes(t.address.toLowerCase()));
  }, [row, grantedNow]);

  // Derived, never written back: a selection that has just been granted falls back to the next
  // missing token instead of pinning step 2 to something already done.
  const selectedToken: ApprovalTokenRef | undefined = useMemo(
    () => missing.find((t) => sameToken(t.address, pickedAddress)) ?? missing[0],
    [missing, pickedAddress],
  );

  const outcome: ApprovalOutcome = auditQuery.isError
    ? "failed"
    : !row
      ? "checking"
      : row.status === "NOT_FOUND" || row.status === "ERROR"
        ? "unavailable"
        : missing.length === 0
          ? "complete"
          : "missing";

  const noticeMsg = useMemo(() => {
    if (outcome === "failed") {
      return apiErrorMessage(
        auditQuery.error,
        `Could not check this ${api.noun}'s approvals. Please try again.`,
      );
    }
    if (outcome === "unavailable") {
      return (
        row?.reason ??
        `This ${api.noun}'s approvals could not be read on-chain.`
      );
    }
    if (outcome === "complete") {
      const names: string = (audit?.tokens ?? [])
        .map((t: ApprovalTokenRef) => tokenRefLabel(t))
        .join(", ");
      return `This ${api.noun} is approved for every supported token${
        names ? ` (${names})` : ""
      }. There is nothing to add.`;
    }
    return "";
  }, [outcome, auditQuery.error, row, audit, api.noun]);

  const recheck = useCallback(() => {
    auditQuery.refetch();
  }, [auditQuery]);

  const selectToken = useCallback((address: string) => {
    setPickedAddress(address);
  }, []);

  const goToGrant = useCallback(() => {
    if (!selectedToken) return;
    setGrantErrorMsg("");
    setStep(2);
  }, [selectedToken]);

  const goBack = useCallback(() => {
    setGrantErrorMsg("");
    setStep(1);
  }, []);

  const grant = useCallback(async () => {
    if (!selectedToken || isGranting) return;
    const token = selectedToken;
    const label = tokenRefLabel(token);
    setGrantErrorMsg("");
    setGrantSuccessMsg("");
    setIsGranting(true);
    try {
      const result: GrantResult = await api.grant(
        id,
        token.address,
        networkType,
      );
      const grantRow: GrantRow | undefined = result?.results?.[0];
      if (!grantRow) {
        setGrantErrorMsg(
          `The server did not report a result for ${api.noun} ${id}. Re-check before retrying.`,
        );
        return;
      }
      if (
        grantRow.status === "GRANTED" ||
        grantRow.status === "ALREADY_APPROVED"
      ) {
        setGrantedNow((prev) => [...prev, token.address.toLowerCase()]);
        setGrantSuccessMsg(
          grantRow.status === "GRANTED"
            ? `${label} approval granted. This ${api.noun} can now support in ${label}.`
            : `This ${api.noun} was already approved for ${label} — nothing was sent.`,
        );
        // The audit is stale from here; refetching also refreshes what `approved` lists.
        queryClient.invalidateQueries({ queryKey: ["approvals", kind, id] });
        return;
      }
      setGrantErrorMsg(
        grantRow.reason ??
          `Could not grant the ${label} approval for ${api.noun} ${id}.`,
      );
    } catch (error: any) {
      setGrantErrorMsg(
        apiErrorMessage(
          error,
          `Failed to grant the ${label} approval. Please try again.`,
        ),
      );
    } finally {
      setIsGranting(false);
    }
  }, [selectedToken, isGranting, api, id, networkType, queryClient, kind]);

  const dismissGrantError = useCallback(() => setGrantErrorMsg(""), []);

  const acknowledgeGrant = useCallback(() => {
    setGrantSuccessMsg("");
    setPickedAddress(undefined);
    setStep(1);
  }, []);

  return {
    step,
    outcome,
    noticeMsg,
    isChecking: auditQuery.isFetching,
    recheck,
    walletAddress: row?.address ?? null,
    subjectStatus: row?.subjectStatus ?? "",
    spender: audit?.spender ?? "",
    approved,
    missing,
    selectedToken,
    selectToken,
    goToGrant,
    goBack,
    isGranting,
    grantErrorMsg,
    grantSuccessMsg,
    grant,
    dismissGrantError,
    acknowledgeGrant,
  };
}
